import { Typography } from '@design-system/components/typography/typography';
import {
  faEye,
  faEyeSlash,
  faReply,
  faTrash,
  faBadgeCheck,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton } from '@design-system/components/button/iconButton';
import { Tooltip } from '@design-system/components/tooltip/tooltip';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { pluralize } from '@client/new/helpers/general';
import { ViewComment } from '../model/comments.viewModel';
import { UserAvatar } from './UserAvatar';
import * as css from './CommentCard.css';

type CommentCardProps = {
  comment: ViewComment;
  onVisibilityToggle: (isVisible: boolean) => void;
  onThreadClick?: () => void;
  onDelete?: () => void;
  showThreadButton?: boolean;
  isInThread?: boolean;
};

export const CommentCard = ({
  comment,
  onVisibilityToggle,
  onThreadClick,
  onDelete,
  showThreadButton = true,
  isInThread = false,
}: CommentCardProps) => {
  const influencerId = useInfluencerId();
  const threadCount = comment.thread?.length || 0;
  const hasThread = threadCount > 0;
  const showThread = showThreadButton && hasThread;
  const isOwnComment = comment.author.userId === Number(influencerId);
  const shouldShowReplyButton =
    comment.shouldShowReplyButton && showThreadButton && onThreadClick;

  return (
    <div className={css.commentItem}>
      <div className={css.commentHeader}>
        <UserAvatar
          src={comment.author.userAvatarURL}
          displayName={comment.displayName}
          isCreator={comment.author.userId === Number(influencerId)}
        />
        <div className={css.authorInfo}>
          <div className={css.authorNameContainer}>
            <Typography
              variant="body"
              size="medium"
              className={css.avenirFontBold}
            >
              {comment.displayName}
            </Typography>
            {isOwnComment && (
              <FontAwesomeIcon
                icon={faBadgeCheck}
                className={css.creatorBadge}
              />
            )}
          </div>
          <Typography
            variant="body"
            size="small"
            fontWeight="normal"
            color="Secondary-900"
            className={css.avenirFont}
          >
            {comment.formattedTime}
          </Typography>
        </div>
      </div>

      <div className={css.commentText}>
        <Typography variant="body" color="B-100" fontWeight="normal">
          {comment.text}
        </Typography>
      </div>

      <div className={css.iconButtonsContainer}>
        {showThread ? (
          <Typography
            variant="body"
            size="small"
            className={css.threadCountText}
            onClick={onThreadClick}
          >
            {threadCount} {pluralize('COMMENT', threadCount).toUpperCase()}
          </Typography>
        ) : (
          <>&nbsp;</>
        )}

        <div className={css.buttonContainer}>
          {isOwnComment && onDelete && (
            <Tooltip variant="dark" title="Delete" enterDelay={300} arrow>
              <span>
                <IconButton
                  icon={faTrash}
                  onClick={onDelete}
                  variant="compact"
                />
              </span>
            </Tooltip>
          )}

          <Tooltip
            variant="dark"
            title={comment.hidden ? 'Show' : 'Hide'}
            enterDelay={300}
            arrow
          >
            <span>
              <IconButton
                icon={comment.hidden ? faEyeSlash : faEye}
                onClick={() => onVisibilityToggle(comment.hidden)}
                variant="compact"
              />
            </span>
          </Tooltip>

          {shouldShowReplyButton && (
            <Tooltip variant="dark" title="Reply" enterDelay={300} arrow>
              <span>
                <IconButton
                  variant="compact"
                  icon={faReply}
                  onClick={onThreadClick}
                />
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

export type { ViewComment };
